import * as XLSX from 'xlsx';
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  where,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { NewMatch, Match } from '../../types';
import { MundialMatchType, MundialTeam, MUNDIAL_LEAGUE } from '../../types/mundial';
import { PENDING_TEAM_LABEL } from '../../constants/mundialBracket';
import { KNOCKOUT_ROUNDS } from '../../constants/mundialBracket';
import { applyDefaultMundialActivation } from '../matchdayService';

export interface ScheduleRowInput {
  date: Date;
  match_type: MundialMatchType;
  group?: string;
  team_a_code?: string;
  team_b_code?: string;
  stadium?: string;
  bracket_slot?: string;
  source_slot_a?: string;
  source_slot_b?: string;
  phase?: string;
}

type ScheduleField =
  | 'date'
  | 'time'
  | 'match_type'
  | 'group'
  | 'team_a_code'
  | 'team_b_code'
  | 'stadium'
  | 'bracket_slot'
  | 'source_slot_a'
  | 'source_slot_b'
  | 'phase';

const HEADER_ALIASES: Record<string, ScheduleField> = {
  fecha: 'date',
  date: 'date',
  fecha_partido: 'date',
  hora: 'time',
  time: 'time',
  hora_partido: 'time',
  tipo: 'match_type',
  type: 'match_type',
  ronda: 'match_type',
  fase_tipo: 'match_type',
  grupo: 'group',
  group: 'group',
  codigo_local: 'team_a_code',
  codigolocal: 'team_a_code',
  local: 'team_a_code',
  cod_local: 'team_a_code',
  codigo_visitante: 'team_b_code',
  codigovisitante: 'team_b_code',
  visitante: 'team_b_code',
  cod_visitante: 'team_b_code',
  estadio: 'stadium',
  stadium: 'stadium',
  sede: 'stadium',
  slot: 'bracket_slot',
  bracket_slot: 'bracket_slot',
  partido: 'bracket_slot',
  origen_a: 'source_slot_a',
  origen_local: 'source_slot_a',
  source_a: 'source_slot_a',
  origen_b: 'source_slot_b',
  origen_visitante: 'source_slot_b',
  source_b: 'source_slot_b',
  fase: 'phase',
  phase: 'phase',
};

const ROUND_TYPE_ALIASES: Record<string, MundialMatchType> = {
  grupo: 'group',
  group: 'group',
  fase_grupos: 'group',
  'fase de grupos': 'group',
  '16vos': 'round_of_32',
  '16vos de final': 'round_of_32',
  dieciseisavos: 'round_of_32',
  r32: 'round_of_32',
  round_of_32: 'round_of_32',
  '8vos': 'round_of_16',
  '8vos de final': 'round_of_16',
  octavos: 'round_of_16',
  r16: 'round_of_16',
  round_of_16: 'round_of_16',
  cuartos: 'quarter_final',
  'cuartos de final': 'quarter_final',
  quarter: 'quarter_final',
  quarter_final: 'quarter_final',
  semifinal: 'semi_final',
  semi: 'semi_final',
  semi_final: 'semi_final',
  final: 'final',
  'tercer lugar': 'third_place',
  third_place: 'third_place',
};

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
}

function normalizeRoundType(raw: string): MundialMatchType | null {
  const key = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return ROUND_TYPE_ALIASES[key] ?? null;
}

function parseExcelSerial(n: number): Date | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  const parsed = XLSX.SSF.parse_date_code(n);
  if (!parsed) return null;
  return new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H || 0, parsed.M || 0);
}

function applyTimeToDate(date: Date, timeRaw?: string): Date {
  if (!timeRaw || timeRaw === '') return date;
  const t = String(timeRaw).trim();
  const hm = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (hm) {
    date.setHours(Number(hm[1]), Number(hm[2]), Number(hm[3] || 0), 0);
    return date;
  }
  const asNum = parseFloat(t);
  if (!Number.isNaN(asNum) && asNum >= 0 && asNum < 1) {
    const totalMinutes = Math.round(asNum * 24 * 60);
    date.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  }
  return date;
}

function parseExcelDate(value: unknown, timeRaw?: string): Date | null {
  if (value === '' || value == null) return null;

  if (value instanceof Date) {
    return applyTimeToDate(new Date(value.getTime()), timeRaw);
  }

  if (typeof value === 'number') {
    const date = parseExcelSerial(value);
    return date ? applyTimeToDate(date, timeRaw) : null;
  }

  const str = String(value).trim();
  if (!str) return null;

  if (/^\d+(\.\d+)?$/.test(str)) {
    const date = parseExcelSerial(parseFloat(str));
    return date ? applyTimeToDate(date, timeRaw) : null;
  }

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return applyTimeToDate(date, timeRaw);
  }

  const dmy4 = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
  if (dmy4) {
    const date = new Date(Number(dmy4[3]), Number(dmy4[2]) - 1, Number(dmy4[1]));
    return applyTimeToDate(date, timeRaw);
  }

  const dmy2 = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})$/);
  if (dmy2) {
    const year = 2000 + Number(dmy2[3]);
    const date = new Date(year, Number(dmy2[2]) - 1, Number(dmy2[1]));
    return applyTimeToDate(date, timeRaw);
  }

  const parsed = Date.parse(str);
  if (!Number.isNaN(parsed)) {
    return applyTimeToDate(new Date(parsed), timeRaw);
  }

  return null;
}

function looksLikeDateValue(value: unknown): boolean {
  if (value instanceof Date) return true;
  if (typeof value === 'number') return value >= 1 && value < 100000;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return false;
    return (
      /^\d{4}-\d{2}-\d{2}/.test(s) ||
      /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}/.test(s) ||
      /^\d{5,}(\.\d+)?$/.test(s)
    );
  }
  return false;
}

const SKIP_SHEET_NAME_PATTERN =
  /^(fuentes|fonts|font|metadata|instrucciones|instruccion|plantilla|readme|hoja\d+|sheet\d+)$/i;

function normalizeSheetName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const SCHEDULE_FIELDS: ScheduleField[] = [
  'date', 'time', 'match_type', 'group', 'team_a_code', 'team_b_code',
  'stadium', 'bracket_slot', 'source_slot_a', 'source_slot_b', 'phase',
];

function resolveFieldFromKey(key: string): ScheduleField | undefined {
  if (SCHEDULE_FIELDS.includes(key as ScheduleField)) {
    return key as ScheduleField;
  }
  return HEADER_ALIASES[normalizeHeader(key)];
}

/** Busca valor de fecha en la fila (por encabezado o primer valor con forma de fecha). */
function findDateInRow(row: Record<string, unknown>): unknown {
  if (row.date !== undefined && row.date !== '' && row.date != null) return row.date;

  for (const [key, value] of Object.entries(row)) {
    if (resolveFieldFromKey(key) === 'date' && value !== '' && value != null) {
      return value;
    }
  }
  for (const value of Object.values(row)) {
    if (looksLikeDateValue(value)) return value;
  }
  return null;
}

function findTimeInRow(row: Record<string, unknown>): string | undefined {
  if (row.time !== undefined && row.time !== '' && row.time != null) {
    return formatTimeValue(row.time);
  }

  for (const [key, value] of Object.entries(row)) {
    if (resolveFieldFromKey(key) === 'time') {
      return formatTimeValue(value);
    }
  }
  return undefined;
}

function formatTimeValue(value: unknown): string | undefined {
  if (value === '' || value == null) return undefined;
  if (typeof value === 'number' && value >= 0 && value < 1) {
    const totalMinutes = Math.round(value * 24 * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return String(value).trim();
}

function getCellValue(sheet: XLSX.WorkSheet, row: number, col: number): unknown {
  const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
  if (!cell) return '';
  if (cell.v instanceof Date) return cell.v;
  if (cell.t === 'n' && typeof cell.v === 'number') return cell.v;
  if (cell.w != null && String(cell.w).trim() !== '') return cell.w;
  return cell.v ?? '';
}

/** Primera fila: índice de columna → campo del calendario */
function getScheduleColumnMap(sheet: XLSX.WorkSheet): Map<number, ScheduleField> | null {
  const ref = sheet['!ref'];
  if (!ref) return null;

  const range = XLSX.utils.decode_range(ref);
  const colMap = new Map<number, ScheduleField>();

  for (let c = range.s.c; c <= range.e.c; c++) {
    const raw = getCellValue(sheet, range.s.r, c);
    if (raw === '' || raw == null) continue;
    const field = HEADER_ALIASES[normalizeHeader(String(raw))];
    if (field) colMap.set(c, field);
  }

  const fields = Array.from(colMap.values());
  if (!fields.includes('date')) return null;
  return colMap;
}

function isScheduleSheet(colMap: Map<number, ScheduleField>): boolean {
  const fields = new Set(colMap.values());
  return (
    fields.has('date') &&
    (fields.has('group') || fields.has('bracket_slot') || fields.has('match_type'))
  );
}

function readDataRow(
  sheet: XLSX.WorkSheet,
  rowIndex: number,
  colMap: Map<number, ScheduleField>
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  colMap.forEach((field, col) => {
    row[field] = getCellValue(sheet, rowIndex, col);
  });
  return row;
}

function isEmptyDataRow(row: Record<string, unknown>): boolean {
  return Object.values(row).every((v) => String(v ?? '').trim() === '');
}

function inferSheetKind(sheetName: string): 'group' | 'knockout' | 'mixed' {
  const n = sheetName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (/eliminatoria|llave|knockout|playoff/.test(n)) return 'knockout';
  if (/fase de grupos|fase grupos|grupos|calendario/.test(n)) return 'group';
  if (/grupo|fase/.test(n) && !/eliminatoria/.test(n)) return 'group';
  return 'mixed';
}

/** Origen de llave: 1A, 2B, 3C o mejor tercero combinado 3ABCDF */
function isBracketOriginSlot(code: string): boolean {
  const c = code.toUpperCase().trim();
  return /^[123][A-L]$/.test(c) || /^3[A-L]{2,}$/i.test(c);
}

function applyKnockoutCodeColumns(mapped: Partial<Record<ScheduleField, string>>): void {
  if (mapped.team_a_code && !mapped.source_slot_a) {
    mapped.source_slot_a = mapped.team_a_code.toUpperCase();
    delete mapped.team_a_code;
  }
  if (mapped.team_b_code && !mapped.source_slot_b) {
    mapped.source_slot_b = mapped.team_b_code.toUpperCase();
    delete mapped.team_b_code;
  }
}

function phaseLabelForType(matchType: MundialMatchType, group?: string, custom?: string): string {
  if (custom) return custom;
  if (matchType === 'group' && group) return `Grupo ${group.toUpperCase()}`;
  if (matchType === 'third_place') return 'Tercer lugar';
  const round = KNOCKOUT_ROUNDS.find((r) => r.matchType === matchType);
  return round?.phaseLabel ?? matchType;
}

interface MapRowContext {
  sheetName: string;
  defaultType: 'group' | 'knockout' | 'mixed';
  errors: string[];
}

function parseScheduleRow(
  row: Record<string, unknown>,
  rowNum: number,
  ctx: MapRowContext
): ScheduleRowInput | null {
  const { sheetName, defaultType, errors } = ctx;
  const mapped: Partial<Record<ScheduleField, string>> = {};

  Object.entries(row).forEach(([key, value]) => {
    const field = resolveFieldFromKey(key);
    if (field && field !== 'date' && field !== 'time') {
      mapped[field] = String(value ?? '').trim();
    }
  });

  const rawDate = findDateInRow(row);
  const timeValue = findTimeInRow(row) ?? mapped.time;
  const date = parseExcelDate(rawDate, timeValue);

  if (!date) {
    const preview = rawDate != null ? String(rawDate) : '(vacío)';
    errors.push(
      `Fila ${rowNum} [${sheetName}]: fecha inválida o vacía (valor: ${preview}). Use AAAA-MM-DD o celda Fecha tipo fecha en Excel`
    );
    return null;
  }

  let matchType: MundialMatchType | null = null;

  if (mapped.phase) {
    const fromPhase = normalizeRoundType(mapped.phase);
    if (fromPhase) matchType = fromPhase;
  }

  if (mapped.match_type) {
    const fromRound = normalizeRoundType(mapped.match_type);
    if (!fromRound) {
      errors.push(`Fila ${rowNum}: ronda/tipo "${mapped.match_type}" no reconocido`);
      return null;
    }
    matchType = fromRound;
  } else if (!matchType) {
    if (defaultType === 'group') {
      matchType = 'group';
    } else if (defaultType === 'knockout') {
      errors.push(`Fila ${rowNum}: falta columna Ronda (ej. round_of_32)`);
      return null;
    } else if (mapped.group) {
      matchType = 'group';
    } else if (mapped.bracket_slot) {
      matchType = 'round_of_32';
    } else {
      errors.push(`Fila ${rowNum}: indica Ronda, Grupo o Slot`);
      return null;
    }
  }

  const group = mapped.group?.toUpperCase();

  if (matchType !== 'group') {
    applyKnockoutCodeColumns(mapped);
  } else if (
    mapped.team_a_code &&
    isBracketOriginSlot(mapped.team_a_code)
  ) {
    applyKnockoutCodeColumns(mapped);
  }

  if (matchType === 'group') {
    if (!group) {
      errors.push(`Fila ${rowNum}: falta el grupo en partido de fase de grupos`);
      return null;
    }
    if (!mapped.team_a_code || !mapped.team_b_code) {
      errors.push(`Fila ${rowNum}: faltan códigos local y visitante`);
      return null;
    }
  }

  if (matchType !== 'group' && !mapped.bracket_slot) {
    errors.push(`Fila ${rowNum}: falta Slot (ej. R32-01, R16-01, FINAL) en eliminatorias`);
    return null;
  }

  if (matchType !== 'group' && !mapped.source_slot_a && !mapped.source_slot_b) {
    errors.push(
      `Fila ${rowNum}: en eliminatorias indica Código local/visitante como origen (ej. 2A, 2B) o columnas OrigenA/B`
    );
    return null;
  }

  const phaseLabel =
    mapped.phase && !normalizeRoundType(mapped.phase) ? mapped.phase : undefined;

  return {
    date,
    match_type: matchType,
    group: matchType === 'group' ? group : undefined,
    team_a_code: mapped.team_a_code?.toUpperCase() || undefined,
    team_b_code: mapped.team_b_code?.toUpperCase() || undefined,
    stadium: mapped.stadium,
    bracket_slot: mapped.bracket_slot?.toUpperCase(),
    source_slot_a: mapped.source_slot_a?.toUpperCase(),
    source_slot_b: mapped.source_slot_b?.toUpperCase(),
    phase: phaseLabel,
  };
}

export function parseScheduleFromExcelBuffer(buffer: ArrayBuffer): {
  rows: ScheduleRowInput[];
  errors: string[];
} {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const rows: ScheduleRowInput[] = [];
  const errors: string[] = [];
  const processedSheets: string[] = [];
  const skippedSheets: string[] = [];

  workbook.SheetNames.forEach((sheetName) => {
    const normalizedName = normalizeSheetName(sheetName);
    if (SKIP_SHEET_NAME_PATTERN.test(normalizedName)) {
      skippedSheets.push(sheetName);
      return;
    }

    const sheet = workbook.Sheets[sheetName];
    const colMap = getScheduleColumnMap(sheet);
    if (!colMap || !isScheduleSheet(colMap)) {
      skippedSheets.push(sheetName);
      return;
    }

    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    const kind = inferSheetKind(sheetName);
    let sheetHadRows = false;

    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const row = readDataRow(sheet, r, colMap);
      if (isEmptyDataRow(row)) continue;

      sheetHadRows = true;
      const parsed = parseScheduleRow(row, r + 1, {
        sheetName,
        defaultType: kind,
        errors,
      });
      if (parsed) rows.push(parsed);
    }

    if (sheetHadRows) processedSheets.push(sheetName);
  });

  if (rows.length === 0 && errors.length === 0) {
    errors.push(
      `No se encontró calendario válido. El archivo tiene hojas: ${workbook.SheetNames.join(', ')}. ` +
        `Usa hojas con columna "Fecha" y "Grupo" o "Ronda"+"Slot" (ej. "Fase de grupos", "Eliminatorias"). ` +
        `Se ignoraron: ${skippedSheets.join(', ') || 'ninguna'}.`
    );
  }

  return { rows, errors };
}

function resolveTeamDisplay(
  code: string | undefined,
  teamsByCode: Map<string, MundialTeam>,
  allowPending: boolean
): { name: string; code: string | null; logo: string | null } {
  if (!code || code === '-' || code === 'TBD') {
    if (!allowPending) throw new Error(`Código de equipo requerido`);
    return { name: PENDING_TEAM_LABEL, code: null, logo: null };
  }
  const team = teamsByCode.get(code.toUpperCase());
  if (!team) throw new Error(`Código "${code}" no existe en selecciones cargadas`);
  return { name: team.name, code: team.code, logo: team.flag_url };
}

export function validateScheduleRows(
  rows: ScheduleRowInput[],
  teams: MundialTeam[]
): string[] {
  const errors: string[] = [];
  const teamsByCode = new Map(teams.map((t) => [t.code.toUpperCase(), t]));
  const slots = new Set<string>();

  rows.forEach((row, i) => {
    const n = i + 2;
    try {
      if (row.match_type === 'group') {
        resolveTeamDisplay(row.team_a_code, teamsByCode, false);
        resolveTeamDisplay(row.team_b_code, teamsByCode, false);
      } else {
        if (row.bracket_slot) {
          if (slots.has(row.bracket_slot)) {
            errors.push(`Fila ${n}: slot duplicado ${row.bracket_slot}`);
          }
          slots.add(row.bracket_slot);
        }
        if (row.team_a_code && !isBracketOriginSlot(row.team_a_code)) {
          resolveTeamDisplay(row.team_a_code, teamsByCode, false);
        }
        if (row.team_b_code && !isBracketOriginSlot(row.team_b_code)) {
          resolveTeamDisplay(row.team_b_code, teamsByCode, false);
        }
      }
    } catch (e) {
      errors.push(`Fila ${n}: ${e instanceof Error ? e.message : 'Error de validación'}`);
    }
  });

  return errors;
}

function rowToMatch(row: ScheduleRowInput, teamsByCode: Map<string, MundialTeam>): Omit<NewMatch, 'id'> {
  const isKnockout = row.match_type !== 'group';
  const teamA = resolveTeamDisplay(row.team_a_code, teamsByCode, isKnockout);
  const teamB = resolveTeamDisplay(row.team_b_code, teamsByCode, isKnockout);
  const now = new Date();

  return {
    league: MUNDIAL_LEAGUE,
    phase: phaseLabelForType(row.match_type, row.group, row.phase),
    match_type: row.match_type,
    group: row.match_type === 'group' ? row.group ?? null : null,
    bracket_slot: row.bracket_slot ?? null,
    source_slot_a: row.source_slot_a ?? null,
    source_slot_b: row.source_slot_b ?? null,
    team_a: teamA.name,
    team_b: teamB.name,
    team_a_code: teamA.code,
    team_b_code: teamB.code,
    team_a_logo: teamA.logo,
    team_b_logo: teamB.logo,
    score_a: null,
    score_b: null,
    activo: false,
    date: row.date,
    is_first_leg: true,
    stadium: row.stadium ?? null,
    created_at: now,
    updated_at: now,
  };
}

async function deleteAllMundialMatches(): Promise<number> {
  const snap = await getDocs(
    query(collection(db, 'matches'), where('league', '==', MUNDIAL_LEAGUE))
  );
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
  return snap.docs.length;
}

export const mundialScheduleService = {
  parseScheduleFromExcelBuffer,
  validateScheduleRows,

  async importSchedule(
    rows: ScheduleRowInput[],
    teams: MundialTeam[],
    replaceExisting = true
  ): Promise<{ imported: number; deleted: number }> {
    const validationErrors = validateScheduleRows(rows, teams);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join('\n'));
    }
    if (rows.length === 0) {
      throw new Error('El archivo no contiene partidos válidos');
    }

    let deleted = 0;
    if (replaceExisting) {
      deleted = await deleteAllMundialMatches();
    }

    const teamsByCode = new Map(teams.map((t) => [t.code.toUpperCase(), t]));
    const matchData = rows.map((row) => rowToMatch(row, teamsByCode));
    const withActivation = applyDefaultMundialActivation(matchData as Match[]);
    const BATCH_LIMIT = 450;
    let batch = writeBatch(db);
    let opCount = 0;
    let imported = 0;

    for (const fixture of withActivation) {
      const ref = doc(collection(db, 'matches'));
      batch.set(ref, fixture);
      opCount += 1;
      imported += 1;

      if (opCount >= BATCH_LIMIT) {
        await batch.commit();
        batch = writeBatch(db);
        opCount = 0;
      }
    }

    if (opCount > 0) await batch.commit();

    return { imported, deleted };
  },
};
